import type {
  MapHistoryPage,
  MapHistorySnapshot,
  MapPublishReview,
  MapSourcePreview,
} from "../shared/history";
import type {
  AdminIdentity,
  AdminMap,
  AdminRole,
  MapDocument,
  MapSummary,
  MapStand,
  PdfImportMetadata,
  PdfImportLogo,
} from "../shared/types";

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
  ) {
    super(message);
  }
}
let identityId = "";
export function logoFileType(file: File): string {
  return file.type || (/\.svg$/i.test(file.name) ? "image/svg+xml" : "");
}
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/admin${path}`, {
    ...options,
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(options.body && !(options.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...(options.method &&
      !["GET", "HEAD"].includes(options.method.toUpperCase())
        ? { "X-Admin-User-Id": identityId }
        : {}),
      ...options.headers,
    },
  });
  if (response.ok && response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => null);
  const payload =
    body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  if (!response.ok || !body) {
    const fallback =
      response.status === 401
        ? "Your session has expired. Reload this page to sign in again."
        : "Unable to connect to Gem. Please try again.";
    throw new ApiError(
      typeof payload.error === "string" ? payload.error : fallback,
      typeof payload.code === "string" ? payload.code : "request_failed",
      response.status,
    );
  }
  return body as T;
}
export const api = {
  identity() {
    return identityId;
  },
  async history(id: string, before?: number) {
    return request<MapHistoryPage>(
      `/maps/${encodeURIComponent(id)}/history${before ? `?before=${before}` : ""}`,
    );
  },
  async historyDocument(id: string, revision: number) {
    return request<MapHistorySnapshot>(
      `/maps/${encodeURIComponent(id)}/history/${revision}`,
    );
  },
  async restore(map: AdminMap, revision: number) {
    return (
      await request<{ map: AdminMap }>(
        `/maps/${encodeURIComponent(map.id)}/history/${revision}/restore`,
        {
          method: "POST",
          body: "{}",
          headers: { "If-Match": String(map.revision) },
        },
      )
    ).map;
  },
  async publishReview(map: AdminMap) {
    return (
      await request<{ review: MapPublishReview }>(
        `/maps/${encodeURIComponent(map.id)}/publish-review`,
      )
    ).review;
  },
  async previewSource(map: AdminMap) {
    return (
      await request<{ preview: MapSourcePreview }>(
        `/maps/${encodeURIComponent(map.id)}/refresh-source/preview`,
        {
          method: "POST",
          body: "{}",
          headers: { "If-Match": String(map.revision) },
        },
      )
    ).preview;
  },
  async cancelSource(mapId: string, token: string) {
    return request<void>(
      `/maps/${encodeURIComponent(mapId)}/refresh-source/cancel`,
      { method: "POST", body: JSON.stringify({ token }) },
    );
  },
  async applySource(map: AdminMap, token: string) {
    return (
      await request<{ map: AdminMap }>(
        `/maps/${encodeURIComponent(map.id)}/refresh-source/apply`,
        {
          method: "POST",
          body: JSON.stringify({ token }),
          headers: { "If-Match": String(map.revision) },
        },
      )
    ).map;
  },
  async me() {
    const { user } = await request<{ user: AdminIdentity }>("/me");
    identityId = user.id;
    return user;
  },
  async maps() {
    return (await request<{ maps: MapSummary[] }>("/maps")).maps;
  },
  async map(id: string) {
    return (await request<{ map: AdminMap }>(`/maps/${encodeURIComponent(id)}`))
      .map;
  },
  async create(document: MapDocument) {
    return (
      await request<{ map: AdminMap }>("/maps", {
        method: "POST",
        body: JSON.stringify(document),
      })
    ).map;
  },
  async importPdf(
    document: MapDocument,
    file: File,
    metadata: PdfImportMetadata,
    logos: PdfImportLogo[] = [],
  ) {
    const body = new FormData();
    body.set("document", JSON.stringify(document));
    body.set("metadata", JSON.stringify(metadata));
    body.set("file", file, file.name);
    if (logos.length) {
      body.set(
        "logos",
        JSON.stringify(
          logos.map((logo, index) => ({
            standId: logo.standId,
            field: `logo-${index}`,
            labelMode: logo.labelMode,
          })),
        ),
      );
      for (const [index, logo] of logos.entries()) {
        const fileName =
          logo.file instanceof File ? logo.file.name : `logo-${index}.png`;
        body.set(`logo-${index}`, logo.file, fileName);
      }
    }
    return (
      await request<{ map: AdminMap }>("/maps/import-pdf", {
        method: "POST",
        body,
      })
    ).map;
  },
  async importSource(id: string, title: string, sourceUrl: string) {
    return (
      await request<{ map: AdminMap }>("/maps/import-source", {
        method: "POST",
        body: JSON.stringify({ id, title, sourceUrl }),
      })
    ).map;
  },
  async refreshSource(map: AdminMap) {
    return (
      await request<{ map: AdminMap }>(
        `/maps/${encodeURIComponent(map.id)}/refresh-source`,
        {
          method: "POST",
          body: "{}",
          headers: { "If-Match": String(map.revision) },
        },
      )
    ).map;
  },
  async linkSource(map: AdminMap, sourceUrl: string) {
    return (
      await request<{ map: AdminMap }>(
        `/maps/${encodeURIComponent(map.id)}/source`,
        {
          method: "POST",
          body: JSON.stringify({ sourceUrl }),
          headers: { "If-Match": String(map.revision) },
        },
      )
    ).map;
  },
  async save(document: MapDocument, revision: number) {
    return (
      await request<{ map: AdminMap }>(
        `/maps/${encodeURIComponent(document.id)}`,
        {
          method: "PUT",
          body: JSON.stringify(document),
          headers: { "If-Match": String(revision) },
        },
      )
    ).map;
  },
  async removeMap(map: AdminMap) {
    return request<void>(`/maps/${encodeURIComponent(map.id)}`, {
      method: "DELETE",
      headers: { "If-Match": String(map.revision) },
    });
  },
  async uploadLogo(mapId: string, file: File) {
    return (
      await request<{ logo: NonNullable<MapStand["logo"]> }>(
        `/maps/${encodeURIComponent(mapId)}/logos`,
        {
          method: "POST",
          body: await file.arrayBuffer(),
          headers: { "Content-Type": logoFileType(file) },
        },
      )
    ).logo;
  },
  async publish(map: AdminMap, publish: boolean) {
    return (
      await request<{ map: AdminMap }>(
        `/maps/${encodeURIComponent(map.id)}/${publish ? "publish" : "unpublish"}`,
        {
          method: "POST",
          body: "{}",
          headers: { "If-Match": String(map.revision) },
        },
      )
    ).map;
  },
  async users() {
    return (await request<{ users: AdminUser[] }>("/users")).users;
  },
  async removeUser(email: string) {
    return (
      await request<{ users: AdminUser[] }>(
        `/users/${encodeURIComponent(email)}`,
        { method: "DELETE" },
      )
    ).users;
  },
  async companies(query: string, email: string, signal?: AbortSignal) {
    return request<{ companies: string[]; suggestedCompany: string | null }>(
      `/users/companies?${new URLSearchParams({ q: query, email })}`,
      { signal },
    );
  },
  async updateUserProfile(
    email: string,
    profile: { name: string; company: string },
  ) {
    return (
      await request<{ users: AdminUser[] }>(
        `/users/${encodeURIComponent(email)}`,
        {
          method: "PATCH",
          body: JSON.stringify(profile),
        },
      )
    ).users;
  },
  async saveUser(
    email: string,
    role: AdminRole,
    profile?: { name?: string; company?: string },
  ) {
    return (
      await request<{ users: AdminUser[] }>("/users", {
        method: "PUT",
        body: JSON.stringify({ email, role, ...profile }),
      })
    ).users;
  },
};
export interface AdminUser {
  email: string;
  name?: string | null;
  company?: string | null;
  role: AdminRole;
}
export function errorText(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}
